import { ColorRole, darkenCssValue } from './darkColors';

// Dark mode for colors Emotion never sees: inline style={{}} props (the whole
// Fast Track app, the Funnel report, recharts tooltips) and SVG paint
// attributes (recharts axes, grid lines, bars). A MutationObserver converts them
// as React writes them and remembers each original, so switching back to light
// restores exactly what React set. Subtrees under [data-color-mode-exempt] are
// skipped (previews that must look the way the recipient sees them).

const STYLE_PROPS: Array<[string, ColorRole]> = [
  ['color', 'fg'],
  ['background-color', 'bg'],
  ['background-image', 'bg'],
  ['border-top-color', 'border'],
  ['border-right-color', 'border'],
  ['border-bottom-color', 'border'],
  ['border-left-color', 'border'],
  ['outline-color', 'border'],
  ['fill', 'svg'],
  ['stroke', 'svg'],
  ['box-shadow', 'shadow'],
  ['caret-color', 'fg'],
  ['text-decoration-color', 'fg'],
];
const SVG_ATTRS = ['fill', 'stroke', 'stop-color'];
const SELECTOR = '[style], [fill], [stroke], [stop-color]';
export const EXEMPT_ATTR = 'data-color-mode-exempt';
const MARK = 'data-cm-dark';

interface Entry {
  orig: string;
  dark: string;
}

const originals = new WeakMap<Element, Map<string, Entry>>();
let observer: MutationObserver | null = null;

function convertStyles(el: HTMLElement | SVGElement, rec: Map<string, Entry>) {
  const style = el.style;
  for (const [prop, role] of STYLE_PROPS) {
    const key = `s:${prop}`;
    const cur = style.getPropertyValue(prop);
    const prev = rec.get(key);
    if (!cur) {
      rec.delete(key);
      continue;
    }
    // Our own write coming back through the observer.
    if (prev && prev.dark === cur) continue;
    const next = darkenCssValue(cur, role);
    if (next === cur) {
      rec.delete(key);
      continue;
    }
    style.setProperty(prop, next, style.getPropertyPriority(prop));
    // Browsers re-serialize colors (#fff -> rgb(255, 255, 255)); remember the
    // stored form so the next mutation is recognized as ours.
    rec.set(key, { orig: cur, dark: style.getPropertyValue(prop) });
  }
}

function convertAttributes(el: SVGElement, rec: Map<string, Entry>) {
  for (const attr of SVG_ATTRS) {
    const key = `a:${attr}`;
    const cur = el.getAttribute(attr);
    const prev = rec.get(key);
    if (!cur) {
      rec.delete(key);
      continue;
    }
    if (prev && prev.dark === cur) continue;
    const next = darkenCssValue(cur, 'svg');
    if (next === cur) {
      rec.delete(key);
      continue;
    }
    el.setAttribute(attr, next);
    rec.set(key, { orig: cur, dark: next });
  }
}

function processElement(el: Element) {
  if (!(el instanceof HTMLElement) && !(el instanceof SVGElement)) return;
  if (el.closest(`[${EXEMPT_ATTR}]`)) return;
  const rec = originals.get(el) ?? new Map<string, Entry>();
  if (el.hasAttribute('style')) convertStyles(el, rec);
  if (el instanceof SVGElement) convertAttributes(el, rec);
  if (rec.size) {
    originals.set(el, rec);
    if (!el.hasAttribute(MARK)) el.setAttribute(MARK, '');
  } else if (el.hasAttribute(MARK)) {
    originals.delete(el);
    el.removeAttribute(MARK);
  }
}

function processTree(root: Element) {
  processElement(root);
  root.querySelectorAll(SELECTOR).forEach(processElement);
}

export function startInlineDarkener(): void {
  if (observer || typeof MutationObserver === 'undefined' || !document.body) return;
  processTree(document.body);
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes') {
        processElement(m.target as Element);
      } else {
        m.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) processTree(node as Element);
        });
      }
    }
  });
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['style', ...SVG_ATTRS],
  });
}

export function stopInlineDarkener(): void {
  observer?.disconnect();
  observer = null;
  document.querySelectorAll(`[${MARK}]`).forEach((el) => {
    const rec = originals.get(el);
    rec?.forEach((entry, key) => {
      const name = key.slice(2);
      if (key.startsWith('s:')) {
        const style = (el as HTMLElement | SVGElement).style;
        // Only put back what is still ours; React may have set a new value since.
        if (style.getPropertyValue(name) === entry.dark) {
          style.setProperty(name, entry.orig, style.getPropertyPriority(name));
        }
      } else if (el.getAttribute(name) === entry.dark) {
        el.setAttribute(name, entry.orig);
      }
    });
    originals.delete(el);
    el.removeAttribute(MARK);
  });
}
